import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Users, Clock, Utensils, Calendar } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import { useStore, type AllYouCanPlan, type Store } from '../../store/useStore'
import { useCourse } from '../../hooks/useCourses'
import { useGoBack } from '../../hooks/useGoBack'
import { api } from '../../services/api'
import ReservationModal from './ReservationModal'
import CancellationPolicyTable from './CancellationPolicyTable'
import { reservationPaymentRules } from '../../lib/storePayment'
import DishListPanel from '../../components/DishListPanel'

/**
 * #737: 食べログ準拠のコース詳細ページ。顧客が個別コースを見て予約するエントリ。
 *
 * Layout (mobile-first):
 *   - 写真スライダー (横スクロール)
 *   - 概要 (price / dishes / party / duration)
 *   - 説明 (description)
 *   - コース内容 (dishList)
 *   - キャンセル規定
 *   - 下部固定の「このコースを予約する」CTA
 *
 * Desktop layout (lg+): 2-column with sticky reservation CTA on right.
 */
export default function CourseDetail() {
  const { id: storeId, courseId } = useParams<{ id: string; courseId: string }>()
  const navigate = useNavigate()
  const { t } = useI18n()
  const { stores } = useStore()
  const { course, loading } = useCourse(storeId, courseId)
  // #1361 戻るは履歴を1つ戻す。行き先を push すると押すたびに履歴が積み上がり、
  // 店舗↔コース詳細を往復しても最初に戻れなくなる。
  const goBack = useGoBack(`/stores/${storeId}`)
  const [reservationOpen, setReservationOpen] = useState(false)
  // #1644 写真ギャラリーの現在ページ (スクロール位置から算出、ドット表示用)。
  // 位置は**子要素の実測 offset** から求める — 固定幅の割り算だと snap-center の
  // センタリング補正と px-4 でズレて、タブレット幅でドットが 1 枚ずれる
  const [photoIdx, setPhotoIdx] = useState(0)
  const galleryRef = useRef<HTMLDivElement>(null)
  const photoScrollRaf = useRef(0)
  // #1644 ボタンでも動かせるようにする (食べログ風)。スワイプできない
  // デスクトップや、横スクロールに気づかない人でも 2 枚目以降に届く
  const scrollToPhoto = (idx: number) => {
    const el = galleryRef.current
    if (!el) return
    const child = el.children[idx] as HTMLElement | undefined
    if (!child) return
    el.scrollTo({
      left: child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2,
      behavior: 'smooth',
    })
  }
  const onGalleryScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    cancelAnimationFrame(photoScrollRaf.current)
    photoScrollRaf.current = requestAnimationFrame(() => {
      const center = el.scrollLeft + el.clientWidth / 2
      let best = 0
      let bestDist = Infinity
      Array.from(el.children).forEach((child, i) => {
        const c = child as HTMLElement
        const dist = Math.abs(c.offsetLeft + c.offsetWidth / 2 - center)
        if (dist < bestDist) {
          bestDist = dist
          best = i
        }
      })
      setPhotoIdx(best)
    })
  }

  // #1353/#1644 実体の放題プラン一覧。コース付属 (allYouCanPlanId = 料金込み) と
  // 予約時に選べる有料オプション (drinkPlanIds) の両方をここから名前・価格・
  // 制限時間に解決する — SSOT はプラン側 (コースに複製させない)。
  // 「どの storeId の結果か」を state に含めて描画時に突き合わせる
  // (effect 内で同期クリアすると cascading render になるため)。
  const [fetchedPlans, setFetchedPlans] = useState<{ storeId: string; plans: AllYouCanPlan[] } | null>(null)
  const planId = course?.allYouCanPlanId
  const needPlans = !!planId || (course?.drinkPlanIds?.length ?? 0) > 0
  useEffect(() => {
    if (!storeId || !needPlans) return
    let cancelled = false
    api.listAllYouCanPlans(storeId)
      .then((r) => {
        if (!cancelled) setFetchedPlans({ storeId, plans: r.plans })
      })
      .catch((err) => console.error('[CourseDetail] all-you-can fetch failed', err))
    return () => { cancelled = true }
  }, [storeId, needPlans])
  const ayPlans = fetchedPlans && fetchedPlans.storeId === storeId ? fetchedPlans.plans : []
  const allYouCanPlan = planId ? ayPlans.find((p) => p.id === planId) ?? null : null
  // 予約時に選べる飲み放題 (実体プラン)。無効化済みは出さない
  const bookableDrinkPlans = (course?.drinkPlanIds ?? [])
    .map((id) => ayPlans.find((p) => p.id === id))
    .filter((p): p is AllYouCanPlan => !!p && p.isActive !== false)

  // Direct-link landings (e.g. share URLs) won't have the store in Zustand yet,
  // so fetch it via API and fall back to Zustand cache. Mirrors StoreDetail/index.tsx.
  const zustandStore = stores.find((s) => s.id === storeId)
  const [apiStore, setApiStore] = useState<Store | null>(null)
  const [storeFetchDone, setStoreFetchDone] = useState(false)
  useEffect(() => {
    if (!storeId) return
    let cancelled = false
    api.getStore(storeId)
      .then((data) => {
        if (!cancelled) setApiStore(data as Store)
      })
      .catch((err) => {
        console.error('[CourseDetail] store fetch failed', err)
      })
      .finally(() => {
        if (!cancelled) setStoreFetchDone(true)
      })
    return () => {
      cancelled = true
    }
  }, [storeId])

  const store: Store | undefined = apiStore || zustandStore
  const storeStillLoading = !store && !storeFetchDone

  if (loading || storeStillLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-text-tertiary font-body">{t('common.loading')}</p>
      </div>
    )
  }

  if (!course || !store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <p className="text-text-primary font-body mb-4">{t('courseDetail.notFound')}</p>
        <button
          onClick={goBack}
          className="text-yokoso-red font-body text-sm tap-effect"
        >
          {t('courseDetail.backToStore')}
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white pb-24 safe-bottom">
      <header className="sticky top-0 z-20 bg-white border-b border-gray-100">
        <div className="flex items-center px-4 py-3">
          <button
            onClick={goBack}
            className="p-2 -ml-2 rounded-lg tap-effect"
            aria-label={t('courseDetail.backToStore')}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="ml-2 font-display text-base font-bold text-text-primary truncate flex-1">
            {store.name}
          </h1>
        </div>
      </header>

      {/* CoursesSection と同じく、photoUrls 欠けでページごと落とさない。
          #1644 ギャラリー UX: スクロールバーを隠し、スナップで 1 枚ずつ止め、
          ドット + ◀▶ ボタンで動かせる (スワイプできないデスクトップでも届く) */}
      {(course.photoUrls?.length ?? 0) > 0 && (
        <div className="relative">
          <div
            ref={galleryRef}
            className="overflow-x-auto scrollbar-hide flex gap-2 px-4 py-3 snap-x snap-mandatory scroll-px-4"
            onScroll={onGalleryScroll}
          >
            {course.photoUrls.map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt=""
                className="w-72 h-48 object-cover rounded-xl flex-shrink-0 snap-center bg-gray-100"
                loading="lazy"
              />
            ))}
          </div>
          {(course.photoUrls?.length ?? 0) > 1 && (
            <>
              {/* 何枚目かを数字で出す（商品の写真・EC の商品ページと同じ形）。
                * 白いドットは明るい写真に溶けて見えなかった。 */}
              <span className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-2.5 py-0.5 text-[11px] font-medium text-white">
                {photoIdx + 1} / {course.photoUrls.length}
              </span>
              {photoIdx > 0 && (
                <button
                  onClick={() => scrollToPhoto(photoIdx - 1)}
                  aria-label={t('courseDetail.photoPrev')}
                  className="tap-effect absolute left-6 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-card"
                >
                  <ChevronLeft size={18} className="text-text-primary" />
                </button>
              )}
              {photoIdx < course.photoUrls.length - 1 && (
                <button
                  onClick={() => scrollToPhoto(photoIdx + 1)}
                  aria-label={t('courseDetail.photoNext')}
                  className="tap-effect absolute right-6 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-card"
                >
                  <ChevronRight size={18} className="text-text-primary" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      <div className="px-4 py-3">
        <h2 className="font-display text-lg font-bold text-text-primary mb-2 leading-tight">
          {course.name}
        </h2>
        {course.description && (
          <p className="font-body text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
            {course.description}
          </p>
        )}
      </div>

      <div className="mx-4 my-3 border border-gray-100 rounded-xl divide-y divide-gray-100">
        <SummaryRow
          label={t('courseDetail.price')}
          value={
            <span className="text-yokoso-red font-display font-bold">
              ¥{course.priceYen.toLocaleString()}
              <span className="text-xs font-body text-text-secondary ml-1">
                / {t('courseDetail.perPerson')}
              </span>
            </span>
          }
        />
        {course.numberOfDishes != null && (
          <SummaryRow
            icon={<Utensils size={16} className="text-text-tertiary" />}
            label={t('courseDetail.dishes')}
            value={`${course.numberOfDishes}${t('courseDetail.dishesUnit')}`}
          />
        )}
        <SummaryRow
          icon={<Users size={16} className="text-text-tertiary" />}
          label={t('courseDetail.party')}
          value={t('courseDetail.partyRange', {
            min: String(course.partySizeRange.min),
            max: String(course.partySizeRange.max),
          })}
        />
        <SummaryRow
          icon={<Clock size={16} className="text-text-tertiary" />}
          label={t('courseDetail.duration')}
          value={`${course.durationMinutes}${t('courseDetail.durationUnit')}`}
        />
      </div>

      {course.dishList && (
        <section className="px-4 py-3">
          <h3 className="font-display text-sm font-bold text-text-primary mb-2 flex items-center gap-1.5">
            <Utensils size={16} />
            {t('courseDetail.dishContent')}
          </h3>
          <DishListPanel dishList={course.dishList} />
        </section>
      )}

      {course.campaign && (
        <section className="px-4 py-3">
          <div className="bg-gold/10 border border-gold/40 rounded-lg p-3">
            <p className="font-display text-sm font-bold text-text-primary mb-1">
              ★ {course.campaign.title}
            </p>
            <p className="font-body text-xs text-text-secondary whitespace-pre-wrap leading-relaxed">
              {course.campaign.description}
            </p>
          </div>
        </section>
      )}

      {/* #1353 セットの飲み放題。表示専用の drinkPlans（宣伝枠）と違い、こちらは
          来店時に卓へ実際に適用され、含まれるドリンクが 0 円で注文できる実体を持つ。
          客が「本当に飲み放題が付くのか」を判断できるよう、制限時間まで明示する。 */}
      {allYouCanPlan && (
        <section className="px-4 py-3">
          <div className="rounded-xl bg-matcha/10 border border-matcha/20 p-3">
            <p className="font-display text-sm font-bold text-matcha">
              {t('courseDetail.allYouCanBadge')}
            </p>
            <p className="font-body text-xs text-text-secondary mt-1">
              {t('courseDetail.allYouCanIncluded', {
                name: allYouCanPlan.name,
                minutes: String(allYouCanPlan.durationMinutes),
              })}
            </p>
          </div>
        </section>
      )}

      {(bookableDrinkPlans.length > 0 || (course.drinkPlans?.length ?? 0) > 0) && (
        <section className="px-4 py-3">
          <h3 className="font-display text-sm font-bold text-text-primary mb-2">
            {t('courseDetail.drinkPlans')}
          </h3>
          <div className="space-y-2">
            {/* #1644 予約時に選べる飲み放題 = 実体プラン (#1353)。名称・価格・
                制限時間はプラン定義そのもの (コース側に複製しない = ズレない) */}
            {bookableDrinkPlans.map((plan) => (
              <div key={plan.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-body text-sm font-semibold text-text-primary">{plan.name}</p>
                  <p className="font-display text-sm font-bold text-yokoso-red whitespace-nowrap">
                    ¥{plan.priceYen.toLocaleString()}
                  </p>
                </div>
                <p className="font-body text-xs text-text-secondary mt-1">
                  {plan.durationMinutes}
                  {t('courseReservation.durationUnit')}
                  {plan.description ? ` ・ ${plan.description}` : ''}
                </p>
                <span className="mt-1.5 inline-block rounded-full bg-matcha/15 px-2 py-0.5 font-body text-[11px] font-semibold text-matcha">
                  {t('courseDetail.drinkPlanBookable')}
                </span>
              </div>
            ))}
            {/* 表示専用の宣伝枠 (自由テキスト)。「選べるように見えて選べない」を
                潰すため当日案内を明示 */}
            {(course.drinkPlans ?? []).map((plan, idx) => (
              <div key={`display-${idx}`} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-body text-sm font-semibold text-text-primary">{plan.name}</p>
                  <p className="font-display text-sm font-bold text-yokoso-red whitespace-nowrap">
                    ¥{plan.priceYen.toLocaleString()}
                  </p>
                </div>
                {plan.description && (
                  <p className="font-body text-xs text-text-secondary mt-1 whitespace-pre-wrap">
                    {plan.description}
                  </p>
                )}
                <p className="mt-1.5 font-body text-[11px] text-text-tertiary">
                  {t('courseDetail.drinkPlanOnsiteOnly')}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {course.selectablePerks && course.selectablePerks.length > 0 && (
        <section className="px-4 py-3">
          <h3 className="font-display text-sm font-bold text-text-primary mb-2">
            {t('courseDetail.selectablePerks')}
          </h3>
          <ul className="space-y-1.5 list-disc list-inside font-body text-sm text-text-primary">
            {course.selectablePerks.map((perk, idx) => (
              <li key={idx}>
                <span className="font-semibold">{perk.label}</span>
                {perk.description && (
                  <span className="text-text-secondary">：{perk.description}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {course.cancellationNote && (
        <section className="px-4 py-3">
          <h3 className="font-display text-sm font-bold text-text-primary mb-2">
            {t('courseDetail.cancellationNote')}
          </h3>
          <p className="font-body text-xs text-text-secondary whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-lg p-3">
            {course.cancellationNote}
          </p>
        </section>
      )}

      {/* #737: プラットフォーム共通のキャンセル料規定 (決済モード別) */}
      <section className="px-4 py-3">
        <CancellationPolicyTable
          variant="detail"
          // #2026 保存値ではなく実際に使われる支払い方法 (現金のみの店は当日払い)
          highlightedMode={reservationPaymentRules(store).paymentTiming}
          onsiteProtected={reservationPaymentRules(store).noShowProtection}
          // #1371 人数未定なので 1 名分で実額を出す。率だけだと判断できない。
          totalAmount={course.priceYen}
        />
      </section>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 safe-bottom">
        <div className="mx-auto max-w-[430px] flex items-center gap-3">
          <div className="flex-1">
            <p className="font-body text-xs text-text-tertiary">{t('courseDetail.priceFrom')}</p>
            <p className="font-display text-lg font-bold text-yokoso-red leading-none">
              ¥{course.priceYen.toLocaleString()}
              <span className="text-xs font-body text-text-secondary ml-1">
                / {t('courseDetail.perPerson')}
              </span>
            </p>
          </div>
          <button
            onClick={() => setReservationOpen(true)}
            className="h-12 px-6 rounded-xl bg-yokoso-red text-white font-display font-bold text-sm tap-effect flex items-center gap-1.5"
          >
            <Calendar size={16} />
            {t('courseDetail.bookCta')}
          </button>
        </div>
      </div>

      {reservationOpen && (
        <ReservationModal
          open={reservationOpen}
          onClose={() => setReservationOpen(false)}
          store={store}
          course={course}
          // #1246: ゲストの予約確定はログインへ (このコース詳細に復帰)
          onRequireLogin={() => {
            sessionStorage.setItem('postLoginRedirect', `/stores/${storeId}/courses/${courseId}`)
            navigate('/login')
          }}
        />
      )}
    </div>
  )
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-text-secondary font-body text-xs">
        {icon}
        {label}
      </div>
      <div className="font-body text-sm text-text-primary text-right">{value}</div>
    </div>
  )
}
